import { motion } from 'framer-motion'
import { Phone, Mail, MapPin, Clock } from 'lucide-react'
import { fadeIn, staggerContainer } from '../utils/motion'

import ContactForm from '../compenents/ContactForm'

import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const Contact = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)
  

  const contactInfo = [
    {
      icon: Phone,
      titleKey: 'contact.phoneLabel',
      details: ['+212 665 117 705', '+212 661 692 022', '+212 522 441 629']
    },
    {
      icon: Mail,
      titleKey: 'contact.emailLabel',
      details: ['contact@texaconsult.com']
    },
    {
      icon: MapPin,
      titleKey: 'contact.addressLabel',
      details: [
        language === 'fr' ? 'Casablanca' : 'Casablanca', 
        language === 'fr' ? 'Maroc' : 'Morocco'
      ]
    },
    {
      icon: Clock,
      titleKey: 'contact.hoursLabel',
      details: [t('contact.hoursValue1'), t('contact.hoursValue2')]
    }
  ]

  return (
    <div className="pt-26">
      {/* Section Hero avec image de fond */}
      <section className="relative py-20 text-white overflow-hidden">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("/images/contact-hero.webp")' // Remplacez par le chemin de votre image
          }}
        >
          {/* Overlay pour améliorer la lisibilité */}
          <div className="absolute inset-0 bg-blue-900/70"></div>
        </div>
        
        {/* Contenu */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              {t('contact.pageTitle')}
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              {t('contact.pageSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                variants={fadeIn('up', index * 0.1)}
                className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 mb-6">
                  <info.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-heading font-bold text-gray-900 mb-4">
                  {t(info.titleKey)}
                </h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-gray-600 mb-1">
                    {detail}
                  </p>
                ))}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-8 h-full">
                <h2 className="text-3xl font-heading font-bold text-gray-900 mb-6">
                  {t('contact.locationTitle')}
                </h2>
                
                {/* Carte de localisation */}
                <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
                  <div className="w-full h-64 bg-gray-200 relative">
                   <iframe
  src="https://www.google.com/maps?q=Angle%20Rue%20Al%20Araar%20et%20Bd%20Lalla%20Yacout%2C%20Immeuble%20A%2C%20Appartement%208%2C%20Casablanca%2C%20Maroc&z=17&output=embed"
  width="100%"
  height="100%"
  style={{ border: 0 }}
  allowFullScreen=""
  loading="lazy"
  title="Localisation Texaconsult"
/>

                  </div>
                  <div className="bg-white p-4">
                    <p className="text-sm text-gray-600 text-center">
                      {t('contact.mapDescription')}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4 p-6 bg-blue-50 rounded-xl">
                    <MapPin size={24} className="text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {t('contact.addressLabel')}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {language === 'fr' 
                          ? 'Angle Rue Al Araar et Bd Lalla Yacout, Immeuble A, Appartement 8, Casablanca, Maroc'
                          : language === 'en'
                          ? 'Corner of Al Araar Street and Lalla Yacout Boulevard, Building A, Apartment 8, Casablanca, Morocco'
                          : 'Esquina Calle Al Araar y Bulevar Lalla Yacout, Edificio A, Apartamento 8, Casablanca, Marruecos'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 p-6 bg-green-50 rounded-xl">
                    <Clock size={24} className="text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {t('contact.hoursLabel')}
                      </h3>
                      <p className="text-gray-700">
                        {t('contact.hoursValue1')}<br />
                        {t('contact.hoursValue2')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 p-6 bg-purple-50 rounded-xl">
                    <Mail size={24} className="text-purple-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {t('contact.emailLabel')}
                      </h3>
                      <p className="text-gray-700">contact@texaconsult.com</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mt-6">
                  {t('contact.locationDesc')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              {t('contact.ctaTitle')}
            </h2>
            <p className="text-xl mb-8">
              {t('contact.ctaText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+212665117705"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <Phone size={20} className="mr-2" />
                {t('contact.callButton')}
              </a>
              <a
                href="mailto:texaconsult1@gmail.com"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors duration-200"
              >
                <Mail size={20} className="mr-2" />
                {t('contact.emailButton')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Contact